/**
 * Run on load (docs/superpowers/specs/2026-10-05-run-on-load-design.md):
 * the project's saved scripts that the Conductor runs every time the
 * project's data opens, and what /initialise-data reports about them.
 * Pure: the web app, the Tab Spec, the Dashboard and the MCP server share it.
 */

/** The saved query file that holds the list. The Conductor reads the same name. */
export const RUN_ON_LOAD_FILE = "onload__scripts"

/** How /initialise-data treats the list: "report" answers JSON, "off" opens the raw data. */
export type InitialiseMode = "report" | "off"

interface RunOnLoadList {
  version: 1
  scripts: string[]
}

/** The list file's body. */
export const serializeRunOnLoadList = (scripts: string[]): string =>
  JSON.stringify({ version: 1, scripts } satisfies RunOnLoadList)

/**
 * The script names of a list file body, given as text or as the object
 * axios parsed. Anything that is not a version 1 list of non-empty names is
 * null, so a damaged file is never taken for an empty list.
 */
export const parseRunOnLoadList = (body: unknown): string[] | null => {
  let value = body
  if (typeof body === "string") {
    try {
      value = JSON.parse(body)
    } catch {
      return null
    }
  }
  if (typeof value !== "object" || value === null || Array.isArray(value)) return null
  const list = value as Record<string, unknown>
  if (list.version !== 1 || !Array.isArray(list.scripts)) return null
  const scripts = list.scripts as unknown[]
  return scripts.every((s) => typeof s === "string" && s.trim() !== "")
    ? (scripts as string[])
    : null
}

export interface RunOnLoadFailure {
  /** The script that failed; "" when the list itself could not be read. */
  script: string
  message: string
}

export interface InitialiseReport {
  instance: string
  /** The scripts that ran, in order. */
  applied: string[]
  failed: RunOnLoadFailure | null
}

/** What a `failed` that is not `{script?, message}` says. */
const REPORT_UNREADABLE = "the Run on load report could not be read"

/**
 * The `failed` of a report, read so it fails closed: null or absent is no
 * failure; anything else is one. A well-formed `{script?, message}` keeps
 * both; otherwise its script is kept when it is a string, and its message
 * when it is a non-empty string (a string or a number `failed` keeps neither).
 */
const readFailure = (f: unknown): RunOnLoadFailure | null => {
  if (f === null || f === undefined) return null
  const parts = typeof f === "object" && !Array.isArray(f) ? (f as Record<string, unknown>) : {}
  return {
    script: typeof parts.script === "string" ? parts.script : "",
    message: typeof parts.message === "string" && parts.message !== "" ? parts.message : REPORT_UNREADABLE,
  }
}

/**
 * The answer of /initialise-data?onload=report. A bare instance id (a
 * Conductor before Run on load, or onload=off) reads as a report with
 * nothing applied. A refusal, an empty answer or anything else is null.
 * A `failed` that cannot be read is a failure, never success.
 */
export const readInitialiseReport = (body: unknown): InitialiseReport | null => {
  if (typeof body === "number") return { instance: String(body), applied: [], failed: null }
  if (typeof body === "string") {
    const text = body.trim()
    if (!text || text.startsWith("{") || /^Error\s+\d+:/i.test(text)) return null
    return { instance: text, applied: [], failed: null }
  }
  if (typeof body !== "object" || body === null || Array.isArray(body)) return null
  const report = body as Record<string, unknown>
  if (typeof report.instance !== "string" || !report.instance.trim()) return null
  const applied = Array.isArray(report.applied)
    ? report.applied.filter((s): s is string => typeof s === "string")
    : []
  return { instance: report.instance.trim(), applied, failed: readFailure(report.failed) }
}

/** The sentence every surface shows for a failed Run on load; the Conductor writes the same one. */
export const runOnLoadFailureText = (failed: RunOnLoadFailure): string =>
  failed.script
    ? `Run on load stopped at "${failed.script}": ${failed.message}`
    : `Run on load failed: ${failed.message}`
